import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usersApi, uploadUrl } from '../../api/client';
import styles from './TopContributors.module.css';

export function TopContributors() {
  const [contributors, setContributors] = useState<
    {
      user: { id: string; username: string; displayName: string; avatarUrl?: string };
      likedCount: number;
      beautifulCount: number;
      coolCount: number;
      sweetPhotoCount: number;
      spotCount: number;
      contributorScore: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersApi.topContributors();
      setContributors(res.contributors);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load top contributors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={styles.page}>
      <h1>All-Time Legends</h1>
      <p className={styles.subtitle}>
        Users ranked by a contributor score that rewards posting spots and the reactions they receive
      </p>
      <p className={styles.legend}>
        Contributor score is calculated per spot as 5 points for posting plus up to 50 bonus points from reactions:
        1 point for each like, and 1.5 points for each beautiful, cool, or sweet photo. Your total score is the sum across all your spots.
      </p>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : contributors.length === 0 ? (
        <p className={styles.empty}>No contributors yet.</p>
      ) : (
        <ol className={styles.list}>
          {contributors.map((c, index) => (
            <li key={c.user.id} className={styles.row}>
              <span className={styles.rank}>{index + 1}</span>
              <Link to={`/user/${c.user.id}`} className={styles.card}>
                {c.user.avatarUrl ? (
                  <img
                    src={c.user.avatarUrl.startsWith('http') ? c.user.avatarUrl : uploadUrl(c.user.avatarUrl)}
                    alt=""
                    className={styles.avatarImg}
                  />
                ) : (
                  <span className={styles.avatar}>{c.user.displayName.charAt(0)}</span>
                )}
                <div className={styles.info}>
                  <span className={styles.displayName}>{c.user.displayName}</span>
                  <span className={styles.counts}>
                    Score: {Math.round(c.contributorScore)} · Spots: {c.spotCount} · ♥ {c.likedCount} · ✨ {c.beautifulCount} · 😎 {c.coolCount} · 📷 {c.sweetPhotoCount}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
